/**
 * AuthGuard — Drop-in wrapper that gates app content behind authentication.
 *
 * Checks auth state from AuthProvider, validates required entitlement,
 * and redirects to Portal login if not authenticated. Shows a restricted
 * state with upgrade CTA if authenticated but missing the required entitlement.
 *
 * Super admins bypass all entitlement checks.
 *
 * The `entitlement` prop accepts either a single key or an array of keys
 * (OR-semantics — user passes if they have AT LEAST ONE). An empty array
 * is treated the same as omitting the prop (no gate at all).
 *
 * @example
 * ```tsx
 * import { AuthGuard } from '@plerity/auth/react';
 *
 * // Single entitlement (most apps)
 * <AuthGuard entitlement="finance" loginUrl="https://app.animasmarketing.com/login">
 *   <FinanceApp />
 * </AuthGuard>
 *
 * // Multi-entitlement OR-gate (e.g., merged Ops + CRM shell — users with
 * // EITHER "ops" or "crm" entitlement can access)
 * <AuthGuard entitlement={["ops", "crm"]} loginUrl="https://app.animasmarketing.com/login">
 *   <OpsShell />
 * </AuthGuard>
 * ```
 */

import { type ReactNode, useEffect, useRef } from "react";
import { isE2EAuthBypass } from "./e2e-bypass.js";
import { formatEntitlements, normalizeEntitlement } from "./entitlement.js";
import { sanitizeReturnTo } from "./returnTo.js";
import { useAuth } from "./useAuth.js";

interface AuthGuardProps {
  /** App content to render when authenticated and entitled */
  children: ReactNode;
  /**
   * Required product entitlement key(s) (e.g., "finance", "crm", "seo").
   *
   * - `string` — user must have this exact entitlement
   * - `string[]` — OR-semantics: user must have AT LEAST ONE of these
   * - `[]` or `undefined` — no entitlement gate (auth-only)
   */
  entitlement?: string | string[];
  /** URL to redirect to when not authenticated */
  loginUrl: string;
  /** Custom loading fallback (default: centered spinner) */
  fallback?: ReactNode;
  /** Custom no-entitlement fallback (default: upgrade CTA card) */
  restrictedFallback?: ReactNode;
  /**
   * Extra origins `return_to` may point at besides the current one (e.g. a
   * sibling shell). The URL is run through {@link sanitizeReturnTo}; if it is
   * rejected the login redirect carries no `return_to`.
   */
  allowedReturnOrigins?: string[];
}

function DefaultLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 dark:border-brand-400" />
    </div>
  );
}

function DefaultRestrictedFallback({ entitlement, hasOrg }: { entitlement?: string | string[]; hasOrg: boolean }) {
  // Three distinct states — each needs different copy and CTA so the user
  // (or their admin) knows exactly what to fix.
  let heading: string;
  let message: string;
  let ctaLabel: string;
  let ctaHref: string;

  if (!hasOrg) {
    // User exists in D1 users but has no org_users row — orphan account.
    // Most common cause: Workspace user was provisioned but never linked to an org.
    heading = "No Organization Found";
    message =
      "Your account hasn't been added to any organization yet. " + "Please contact your administrator to be added.";
    ctaLabel = "Go to Portal";
    ctaHref = "https://app.animasmarketing.com";
  } else if (entitlement && normalizeEntitlement(entitlement) !== null) {
    // User is in an org, but the org doesn't have any of the required entitlements enabled.
    heading = "Access Restricted";
    message = `Your organization doesn't have an active ${formatEntitlements(entitlement)} subscription.`;
    ctaLabel = "View Available Apps";
    ctaHref = "https://app.animasmarketing.com/apps";
  } else {
    // Catch-all — authenticated but no specific entitlement required and still blocked.
    heading = "Access Restricted";
    message = "You don't have access to this application.";
    ctaLabel = "View Available Apps";
    ctaHref = "https://app.animasmarketing.com/apps";
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm max-w-sm w-full">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">{heading}</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
        <a
          href={ctaHref}
          className="inline-block px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-hover dark:bg-brand-500 dark:hover:bg-brand-600 transition-colors"
        >
          {ctaLabel}
        </a>
      </div>
    </div>
  );
}

export function AuthGuard({
  children,
  entitlement,
  loginUrl,
  fallback,
  restrictedFallback,
  allowedReturnOrigins,
}: AuthGuardProps) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const bypass = isE2EAuthBypass();
  const needsLogin = !bypass && !isLoading && (!isAuthenticated || !user);
  const redirectedRef = useRef(false);

  // Redirect to login in an effect — assigning `window.location.href` during
  // render is a side effect (it re-fires on every re-render and breaks under
  // StrictMode / concurrent rendering). Once per unauthenticated spell.
  useEffect(() => {
    if (!needsLogin) {
      redirectedRef.current = false;
      return;
    }
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    const returnTo = sanitizeReturnTo(window.location.href, allowedReturnOrigins, window.location.origin);
    const separator = loginUrl.includes("?") ? "&" : "?";
    window.location.href = returnTo ? `${loginUrl}${separator}return_to=${encodeURIComponent(returnTo)}` : loginUrl;
  }, [needsLogin, loginUrl, allowedReturnOrigins]);

  // E2E bypass — render children without auth checks. Resolved from the
  // BUILD-TIME `__E2E_AUTH_BYPASS__` constant (false + elided in prod), never a
  // runtime-settable window global. See e2e-bypass.ts (S02 F-5).
  if (bypass) {
    return <>{children}</>;
  }

  // Still loading auth state, or not authenticated and redirecting to login
  if (isLoading || !isAuthenticated || !user) {
    return <>{fallback ?? <DefaultLoadingFallback />}</>;
  }

  // Authenticated but missing required entitlement (super admins bypass).
  // OR-semantics: when `entitlement` is an array, user passes if they have
  // AT LEAST ONE matching entitlement. Empty array or undefined = no gate.
  const required = normalizeEntitlement(entitlement);
  const lacksAccess =
    required !== null && !user.isSuperAdmin && !required.some((key) => user.entitlements.includes(key));
  if (lacksAccess) {
    return (
      <>{restrictedFallback ?? <DefaultRestrictedFallback entitlement={entitlement} hasOrg={user.orgId !== null} />}</>
    );
  }

  // Authenticated and entitled — render children
  return <>{children}</>;
}
